"use client";

import SignupForm from "@/app/(auth)/signup/signup-form";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  PortalName,
  withRenderPortal,
  type AnonymousSignupModalPortalData,
  type WithRenderPortalProps,
} from "@/context/portal-context";
import { useMemo } from "react";

export interface AnonymousSignupModalProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
}

function AnonymousSignupModalBase({
  open,
  onClose,
  onOpenChange,
  data,
}: AnonymousSignupModalProps & WithRenderPortalProps) {
  const handleOpenChange = (next: boolean) => {
    if (!next) onClose();
    onOpenChange?.(next);
  };

  const portalData = data as AnonymousSignupModalPortalData | undefined;
  const reason = portalData?.reason;

  const { title, description } = useMemo(() => {
    if (reason === "after_message") {
      return {
        title: "Save your chats with a free account",
        description:
          "You’re all set to keep chatting. Create an account so your characters and conversations stay with you across devices and browsers.",
      };
    }
    if (reason === "after_checkout") {
      return {
        title: "Secure your new subscription",
        description:
          "Your plan is active on this session. Sign up so you can sign in anytime and never lose access if you clear cookies or switch devices.",
      };
    }
    return {
      title: "Create a free account (recommended)",
      description:
        "Link your subscription and chats to an email or Google account so nothing is lost if this browser session ends.",
    };
  }, [reason]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        widthClassName="sm:max-w-md"
        className="flex flex-col gap-6 p-6"
        showCloseButton
      >
        <div className="space-y-2 text-center">
          <DialogTitle className="text-2xl font-bold">{title}</DialogTitle>
          <p className="text-muted-foreground text-sm">{description}</p>
        </div>

        <SignupForm afterSignup={() => handleOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

export const AnonymousSubscribeModalPortal = withRenderPortal<
  AnonymousSignupModalProps & WithRenderPortalProps
>(PortalName.anonymous_signup_modal)(AnonymousSignupModalBase);
